/**
 * PriceInput Component - Port from legacy with shadcn UI primitives
 *
 * Direct port of legacy-app/components/PriceInput with:
 * - Exact same logic and behavior
 * - shadcn Input and Label components
 * - TypeScript instead of JavaScript
 * - Functional components instead of class components
 * - lucide-react icons instead of CSS arrows
 *
 * Supports both Decimal and Fractional formats with full UX parity.
 */

import React, { useState, useRef, useEffect, forwardRef } from 'react'
import { cn } from '../../utils'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { ChevronUp, ChevronDown } from 'lucide-react'

export type PriceFormatType = 'Decimal' | 'Fractional'

export interface PriceInputProps {
  // Value and handlers
  value: number | undefined
  onChange: (value: number | undefined) => void
  onBlur?: (e: React.FocusEvent) => void
  onFocus?: (e: React.FocusEvent) => void

  // Price configuration
  format: number
  formatType: PriceFormatType
  tickSize: number

  // Constraints
  max?: number
  min?: number

  // UI
  label?: string
  className?: string
  disabled?: boolean
}

// ============================================
// HELPER FUNCTIONS (Direct ports from legacy)
// ============================================

/**
 * Extract numerical characters and normalize decimal separator
 * Port of: legacy/PriceInDecimalFormat.js numerical()
 */
function numerical(value: string): string {
  if (value === '') return ''

  // Extract numerical characters and "." & ","
  let result = value.replace(/[^\d.,-]/g, '')

  // Unify decimal separator
  result = result.replace(',', '.')

  // Allow only one decimal separator
  const parts = result.split('.')
  if (parts.length === 1) {
    return parts[0] || ''
  }

  const first = parts.shift() || ''
  return `${first}.${parts.join('')}`
}

/**
 * Format integer part (preserves "-" for negative < 1)
 * Port of: legacy/helpers.js formatInteger()
 */
function formatInteger(num: number | undefined): string {
  if (num === undefined || num === null) return ''
  return num.toString().split('.')[0] || ''
}

/**
 * Format decimal part
 * Port of: legacy/helpers.js formatDecimal()
 * (Kept for future use)
 */
// function formatDecimal(num: number | undefined, toNumber: number): string {
//   if (num === undefined || num === null) return ''
//   return num.toFixed(toNumber).split('.')[1] || ''
// }

/**
 * Format fractional numerator (e.g., 16 for 16/32)
 * Port of: legacy/helpers.js formatNumerator()
 */
function formatNumerator(
  value: number | undefined,
  power: number,
  tickSize: number,
  _suppressSign = false
): string {
  if (value === undefined || value === null) return ''

  // Get fractional part
  const intPart = Math.floor(Math.abs(value))
  const fracPart = Math.abs(value) - intPart

  // Calculate denominator (2^power)
  const denominator = Math.pow(2, power)

  // Convert fractional part to numerator
  let numerator = Math.round(fracPart * denominator)

  // Handle special case for half-ticks
  const tickExponent = Math.round(-Math.log(tickSize) / Math.LN2)
  if (tickExponent > power + 0.00001) {
    // Need fractional display (e.g., "127.5" for half of 128th)
    const hasHalf = (numerator * 2) % 2 !== 0
    if (hasHalf) {
      return `${Math.floor(numerator)}.5`
    }
  }

  return numerator.toString()
}

/**
 * Validate price against tick size
 * Port of: Tradovate validationAPI.isPriceValid()
 */
function isPriceValid(value: number | undefined, tickSize: number): boolean {
  if (value === undefined || value === null) return true
  if (!tickSize) return true

  const remainder = Math.abs(value % tickSize)
  const epsilon = tickSize / 1000
  return remainder < epsilon || Math.abs(remainder - tickSize) < epsilon
}

// ============================================
// DECIMAL FORMAT COMPONENT
// ============================================

interface DecimalFormatProps {
  editing: boolean
  format: number
  isValid: boolean
  tickSize: number
  value: number | undefined
  onChange: (value: string) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  className?: string
  disabled?: boolean
}

const PriceInDecimalFormat = forwardRef<HTMLInputElement, DecimalFormatProps>((props, ref) => {
  const { editing, format, isValid, tickSize, value, onChange, className, ...inputProps } = props

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(numerical(e.target.value))
  }

  const formattedValue = (): string => {
    if (value === undefined) return ''

    if (editing || !isValid) {
      return value.toString()
    }

    const decimalPartRequired = tickSize % 1 !== 0

    if (!decimalPartRequired) {
      return parseInt(value.toString(), 10).toString()
    }

    return parseFloat(value.toString()).toFixed(format)
  }

  return (
    <Input
      ref={ref}
      value={formattedValue()}
      onChange={handleChange}
      className={cn('font-mono tabular-nums', className)}
      {...inputProps}
    />
  )
})

PriceInDecimalFormat.displayName = 'PriceInDecimalFormat'

// ============================================
// FRACTIONAL FORMAT COMPONENT
// ============================================

interface FractionalFormatProps {
  editing: boolean
  format: number
  isValid: boolean
  tickSize: number
  value: number | undefined
  onChange: (value: number) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  className?: string
  disabled?: boolean
}

const PriceInFractionalFormat = forwardRef<HTMLDivElement, FractionalFormatProps>((props, ref) => {
  const { editing, format, isValid, tickSize, value, onChange, className, ...inputProps } = props
  const [editedFraction, setEditedFraction] = useState<string | undefined>(undefined)
  const leftRef = useRef<HTMLInputElement>(null)
  const rightRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!editing) {
      setEditedFraction(undefined)
    }
  }, [editing])

  const onIntegerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = (value || 0).toString().split('.')
    const matched = /^-?\d+$/.test(e.target.value)

    if (matched) {
      const newValue = `${e.target.value}.${currentValue[1] || '0'}`
      onChange(parseFloat(newValue))
    } else {
      onChange(value || 0)
    }
  }

  const onDecimalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/[^\d]/g, '')
    let val = parseInt(inputValue) || 0

    setEditedFraction(e.target.value)

    // Calculate max length for numerator display
    const sampleNumerator = formatNumerator(0, format, tickSize)
    const maxLen = String(sampleNumerator).length

    // Pad numerator string
    let numeratorStr: string
    if (val.toString().length < maxLen) {
      numeratorStr = String(`${val}000`).substr(0, maxLen)
    } else {
      numeratorStr = val.toString()
    }

    const tickExponent = Math.round(-Math.log(tickSize) / Math.LN2)
    const denominator = Math.pow(2, format)

    let decimal: number
    if (tickExponent > format + 0.00001) {
      // Handle half-ticks (128ths+)
      const last = numeratorStr[numeratorStr.length - 1]
      if (last === '2' || last === '7') {
        numeratorStr += '5'
      } else {
        numeratorStr += '0'
      }
      numeratorStr = `${numeratorStr.substr(0, numeratorStr.length - 2)}.${numeratorStr.substr(numeratorStr.length - 2, 2)}`
      const numerator = Number(numeratorStr)
      decimal = numerator / denominator
    } else {
      const numerator = Number(numeratorStr)
      decimal = numerator / denominator
    }

    if (decimal >= 1) {
      decimal -= Math.floor(decimal)
    }

    const currentValue = (value || 0).toString().split('.')
    const finalValue = parseInt(currentValue[0] || '0') + decimal

    onChange(parseFloat(finalValue.toString()))
  }

  const formattedFractionalPart = (): string => {
    if (value === undefined) return ''

    if (editedFraction !== undefined && (editing || !isValid)) {
      return editedFraction
    }

    return formatNumerator(value, format, tickSize, true)
  }

  // Keyboard navigation helpers
  const handleIntendedFocusLeft = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const code = e.keyCode || e.which
    const target = e.currentTarget

    // Jump to fractional on Space|Decimal|Period
    if (code === 32 || code === 110 || code === 190) {
      e.preventDefault()
      rightRef.current?.focus()
      rightRef.current?.setSelectionRange(0, 0)
    }

    // Jump to fractional on Right Arrow (at end)
    if (target.selectionStart === target.value.length && code === 39) {
      e.preventDefault()
      rightRef.current?.focus()
      rightRef.current?.setSelectionRange(0, 0)
    }
  }

  const handleIntendedFocusRight = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const code = e.keyCode || e.which
    const target = e.currentTarget

    // Jump to integer on Left Arrow (at start)
    if (target.selectionStart === 0 && code === 37) {
      e.preventDefault()
      const len = leftRef.current?.value.length || 0
      leftRef.current?.focus()
      leftRef.current?.setSelectionRange(len, len)
    }
  }

  const sampleNumerator = formatNumerator(0, format, tickSize)

  return (
    <div ref={ref} className={cn('flex items-center gap-1', className)}>
      <Input
        ref={leftRef}
        value={value === undefined ? '' : formatInteger(value)}
        onChange={onIntegerChange}
        onFocus={inputProps.onFocus}
        onBlur={inputProps.onBlur}
        onKeyDown={handleIntendedFocusLeft}
        disabled={inputProps.disabled}
        className="font-mono tabular-nums text-right flex-1"
      />
      <div className="text-muted-foreground">'</div>
      <Input
        ref={rightRef}
        value={value === undefined ? '' : formattedFractionalPart()}
        onChange={onDecimalChange}
        onFocus={inputProps.onFocus}
        onBlur={inputProps.onBlur}
        onKeyDown={handleIntendedFocusRight}
        disabled={inputProps.disabled}
        maxLength={sampleNumerator.length}
        className="font-mono tabular-nums"
        style={{ width: `${8 * String(sampleNumerator).length + 32}px` }}
      />
    </div>
  )
})

PriceInFractionalFormat.displayName = 'PriceInFractionalFormat'

// ============================================
// MAIN PRICEINPUT COMPONENT
// ============================================

export const PriceInput = forwardRef<HTMLInputElement, PriceInputProps>((props, ref) => {
  const {
    format,
    formatType,
    tickSize,
    max = Number.POSITIVE_INFINITY,
    min = Number.NEGATIVE_INFINITY,
    value,
    onChange,
    onBlur,
    onFocus,
    label,
    className = '',
    disabled = false,
  } = props

  const [editing, setEditing] = useState(false)
  const [localValue, setLocalValue] = useState(value)
  const [highlight, setHighlight] = useState(false)

  // Sync external value changes (unless editing)
  useEffect(() => {
    if (!editing) {
      setLocalValue(value)
    }
  }, [value, editing, tickSize])

  const isValid = isPriceValid(localValue, tickSize)

  const changeValue = (newValue: number | undefined) => {
    setLocalValue(newValue)
    onChange(newValue)
  }

  const handleBlur = (e: React.FocusEvent) => {
    setHighlight(false)
    onBlur?.(e)

    const val = parseFloat(localValue?.toString() || '')

    if (isNaN(val)) {
      changeValue(undefined)
    } else {
      changeValue(val)
      if (isPriceValid(val, tickSize)) {
        setEditing(false)
      }
    }
  }

  const handleChange = (val: string | number) => {
    setLocalValue(typeof val === 'string' ? parseFloat(val) : val)
    setEditing(true)
    onChange(typeof val === 'string' ? parseFloat(val) : val)
  }

  const handleFocus = (e: React.FocusEvent) => {
    setHighlight(true)
    onFocus?.(e)
  }

  const modifyByTick = (action: 'increment' | 'decrement') => () => {
    if (disabled) return

    if (isNaN(localValue as any) || tickSize === 0) {
      changeValue(action === 'increment' ? +tickSize : -tickSize)
    } else {
      let newValue: number

      if (isPriceValid(localValue, tickSize)) {
        // Already valid - just add/subtract tick
        newValue = (localValue || 0) / tickSize
        newValue += action === 'increment' ? +1 : -1
        newValue = Math.round(newValue) * tickSize
      } else {
        // Invalid - round to nearest tick first
        if (action === 'increment') {
          newValue = Math.ceil((localValue || 0) / tickSize) * tickSize
        } else {
          newValue = Math.floor((localValue || 0) / tickSize) * tickSize
        }
      }

      // Apply min/max
      newValue = Math.max(min, Math.min(max, newValue))
      changeValue(newValue)
    }

    setEditing(false)
  }

  // Choose format component
  const InputComponent = formatType === 'Decimal'
    ? PriceInDecimalFormat
    : PriceInFractionalFormat

  const inputProps = {
    editing,
    format,
    tickSize,
    value: localValue,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    isValid,
    disabled,
    className: cn(highlight && 'ring-2 ring-ring'),
  }

  const containerClasses = cn(
    'relative inline-flex',
    !isValid && 'has-error',
    className
  )

  const input = (
    <div className={containerClasses}>
      <div className="relative flex items-center">
        <InputComponent ref={ref as any} {...inputProps} />

        {/* Increment/Decrement controls (matches legacy numeric-input-value-controls) */}
        <div className="absolute inset-y-0 right-0 flex flex-col border-l border-border">
          <button
            type="button"
            className={cn(
              'flex items-center justify-center px-2 flex-1',
              'text-muted-foreground hover:text-foreground hover:bg-accent',
              'transition-colors rounded-tr-md',
              disabled && 'opacity-disabled cursor-not-allowed'
            )}
            onClick={modifyByTick('increment')}
            disabled={disabled}
            tabIndex={-1}
            aria-label="Increment"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <div className="h-px bg-border" />
          <button
            type="button"
            className={cn(
              'flex items-center justify-center px-2 flex-1',
              'text-muted-foreground hover:text-foreground hover:bg-accent',
              'transition-colors rounded-br-md',
              disabled && 'opacity-disabled cursor-not-allowed'
            )}
            onClick={modifyByTick('decrement')}
            disabled={disabled}
            tabIndex={-1}
            aria-label="Decrement"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Validation error (matches legacy has-error feedback) */}
      {!isValid && !editing && (
        <p className="text-sm text-destructive mt-1">
          Price must be a multiple of {tickSize}
        </p>
      )}
    </div>
  )

  // Wrap in label if provided
  if (label) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        {input}
      </div>
    )
  }

  return input
})

PriceInput.displayName = 'PriceInput'
